/* Copied from https://usehooks-ts.com/react-hook/use-local-storage */
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";

declare global {
  interface WindowEventMap {
    "local-storage": CustomEvent;
  }
}

type UseLocalStorageOptions<T> = {
  serializer?: (value: T) => string;
  deserializer?: (value: string) => T;
  initializeWithValue?: boolean;
};

const IS_SERVER = typeof window === "undefined";

// Minimal replacement for useEventCallback
function useEventCallback<T extends (...args: any[]) => any>(fn: T): T {
  const ref = useRef(fn);
  useEffect(() => {
    ref.current = fn;
  }, [fn]);

  return useCallback(((...args: any[]) => ref.current(...args)) as T, []);
}

// Minimal replacement for useEventListener
function useEventListener<K extends keyof WindowEventMap>(
  eventName: K,
  handler: (event: WindowEventMap[K]) => void,
  element: Window | Document = window
) {
  const savedHandler = useRef(handler);

  useEffect(() => {
    savedHandler.current = handler;
  }, [handler]);

  useEffect(() => {
    if (!element?.addEventListener) return;
    const eventListener = (event: Event) => savedHandler.current(event as any);
    element.addEventListener(eventName, eventListener);
    return () => {
      element.removeEventListener(eventName, eventListener);
    };
  }, [eventName, element]);
}

export function useLocalStorage<T>(
  key: string,
  initialValue: T | (() => T),
  options: UseLocalStorageOptions<T> = {}
): [T, Dispatch<SetStateAction<T>>, () => void] {
  const { initializeWithValue = true } = options;

  const serializer = useCallback<(value: T) => string>(
    (value) => {
      if (options.serializer) return options.serializer(value);

      // If it's a string, store it as-is (don't double-stringify)
      if (typeof value === "string") return value;

      // For other types, use JSON.stringify
      return JSON.stringify(value);
    },
  [options]
  );

  const deserializer = useCallback<(value: string) => T>(
    (value) => {
      if (options.deserializer) return options.deserializer(value);
      if (value === "undefined") return undefined as unknown as T;

      // If it's a JSON-stringified string (wrapped in quotes), parse it to get the actual string
      if (value.startsWith('"') && value.endsWith('"') && value.length > 1) {
        try {
          return JSON.parse(value) as T;
        } catch {
          // If parsing fails, return without quotes
          return value.slice(1, -1) as unknown as T;
        }
      }

      // If the stored value looks like JSON objects/arrays, parse them
      if (value.startsWith("{") || value.startsWith("[")) {
        try {
          return JSON.parse(value) as T;
        } catch (error) {
          console.warn(
            `Error parsing JSON for key "${key}", returning raw value:`,
            error
          );
          return value as unknown as T;
        }
      }

      // For boolean and null literals
      if (value === "true" || value === "false" || value === "null") {
        try {
          return JSON.parse(value) as T;
        } catch {
          return value as unknown as T;
        }
      }

      // For numbers
      if (/^-?\d+\.?\d*$/.test(value)) {
        const num = Number(value);
        if (!Number.isNaN(num)) {
          return num as unknown as T;
        }
      }

      // For everything else (plain strings, tokens, etc.), return as-is
      return value as unknown as T;
    },
  [options, key]
  );

  const readValue = useCallback((): T => {
    const initialValueToUse =
      typeof initialValue === "function" ? (initialValue as () => T)() : initialValue;
    if (IS_SERVER) return initialValueToUse;

    try {
      const raw = window.localStorage.getItem(key);
      return raw ? deserializer(raw) : initialValueToUse;
    } catch (error) {
      console.warn(`Error reading localStorage key “${key}”:`, error);
      return initialValueToUse;
    }
  }, [initialValue, key, deserializer]);

  const [storedValue, setStoredValue] = useState<T>(() => {
    if (initializeWithValue) return readValue();
    return typeof initialValue === "function" ? (initialValue as () => T)() : initialValue;
  });

  const setValue: Dispatch<SetStateAction<T>> = useEventCallback((value) => {
    if (IS_SERVER) {
      console.warn(`Tried setting localStorage key “${key}” on server`);
      return;
    }

    try {
      const newValue = typeof value === "function" ? (value as (v: T) => T)(readValue()) : value;
      window.localStorage.setItem(key, serializer(newValue));
      setStoredValue(newValue);
      window.dispatchEvent(new StorageEvent("local-storage", { key }));
    } catch (error) {
      console.warn(`Error setting localStorage key “${key}”:`, error);
    }
  });

  const removeValue = useEventCallback(() => {
    if (IS_SERVER) {
      console.warn(`Tried removing localStorage key “${key}” on server`);
      return;
    }

    const defaultValue =
      typeof initialValue === "function" ? (initialValue as () => T)() : initialValue;
    window.localStorage.removeItem(key);
    setStoredValue(defaultValue);
    window.dispatchEvent(new StorageEvent("local-storage", { key }));
  });

  useEffect(() => {
    setStoredValue(readValue());
  }, [key, readValue]);

  const handleStorageChange = useCallback(
    (event: StorageEvent | CustomEvent) => {
      if ((event as StorageEvent).key && (event as StorageEvent).key !== key)
        return;
      setStoredValue(readValue());
    },
    [key, readValue]
  );

  useEventListener("storage", handleStorageChange);
  useEventListener("local-storage", handleStorageChange);

  return [storedValue, setValue, removeValue];
}
